/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-multi-comp */
import React from "react"
import { useFormContext } from "react-hook-form"
import { Button } from "@mantine/core"

import { DatePickerInput, Icon, MultiSelect, NumberInput, Select, TextareaInput } from "@/components"

import { parseOptions } from "shared/customFields/constants/helperFunctions"
import { useCustomFields } from "@/components/CustomFieldsFormSection/queries"
import { useStyles } from "@/components/CustomFieldsFormSection/CustomFieldsFormSection.styles"

import type { CustomField as CustomFieldType } from "@/types/djangio"

import { checkPermission } from "shared/imports/permissionFunctions"
import { urlInputValidation } from "app/static/frontend/forms/helperFunctions.js"
import { useResponsiveSize } from "@/hooks"

const { CustomTag } = DjangIO.app.grassroots.models
const { TagType } = DjangIO.app.person.types

// Get the default value for a custom field
const TAG_TYPE_DEFAULT_VALUE_MAP = {
    [TagType.number.value]: "default_string_value",
    [TagType.string.value]: "default_string_value",
    [TagType.single_option_list.value]: "default_string_value",
    [TagType.multi_options_list.value]: "default_multi_value",
    [TagType.boolean.value]: "default_boolean_value",
    [TagType.date.value]: "default_string_value",
}

export const CustomField = ({
    disabled,
    parentCustomFieldSlug,
    parentCustomFieldType,
    ...customField
}: CustomFieldType & {
    parentCustomFieldSlug: string
    parentCustomFieldType: number
    disabled: boolean
}) => {
    const formContext = useFormContext()
    const { classes } = useStyles()

    if (parentCustomFieldSlug) {
        const parentCustomField = formContext.watch(`tagDict.${parentCustomFieldSlug}`)

        switch (parentCustomFieldType) {
            case TagType.number.value:
            case TagType.string.value:
                if (parentCustomField !== String(customField.conditional_string_value)) {
                    return null
                }
                break
            case TagType.single_option_list.value:
                if (!customField.conditional_list_value.some((value) => parentCustomField === value)) {
                    return null
                }
                break
            case TagType.multi_options_list.value:
                if (!customField.conditional_list_value.some((value) => parentCustomField?.includes(value))) {
                    return null
                }
                break
            case TagType.boolean.value:
                if (parentCustomField !== customField.conditional_boolean_value) {
                    return null
                }
                break
            case TagType.date.value:
                // TODO: There's no logic implemented for a child to be conditional on a date field
                break
            default:
        }
    }
    const inputProps = {
        id: customField.slug,
        name: `tagDict.${customField.slug}`, // Nest the custom field under the tagDict key
        label: customField.name,
        tooltip: customField.description,
        withAsterisk: customField.is_required,
        rules: { required: customField.is_required ? `${customField.name} is a required field` : false },
        shouldUnregister: true, // Unregister the field when it's removed from the DOM so it doesn't get submitted
        disabled: disabled,
    }
    const getDefaultValue = () => {
        let dv = customField[TAG_TYPE_DEFAULT_VALUE_MAP[customField.tag_type]]
        // if the cf is a single or multi option select the dv might be saved as the option label instead of slug
        if (
            customField.tag_type === TagType.multi_options_list.value ||
            customField.tag_type === TagType.single_option_list.value
        ) {
            const defaultValueSlugs = Object.keys(customField.options_new || {})
                .filter((key) => dv?.includes(customField.options_new[key].value))
                .map((key) => key)
            dv = defaultValueSlugs.concat(dv)
            // single option select expects a single string value, not an array. We do one last check for single_option_list tagType
            dv = customField.tag_type === TagType.single_option_list.value ? dv[0] : dv
        }
        return dv
    }
    const defaultValue = getDefaultValue()

    switch (customField.tag_type) {
        case TagType.number.value:
            return (
                <NumberInput
                    {...inputProps}
                    hideControls
                    // defaultValue may be 0
                    defaultValue={defaultValue != null && defaultValue !== "" ? Number(defaultValue) : undefined}
                    transform={{
                        // Read any value as a number
                        input: (input?: number | string | "" | null) => {
                            if (input === "" || input == null) {
                                return ""
                            }
                            return Number(input)
                        },
                        // Return any value as a string
                        output: (output: number | "") => {
                            return String(output)
                        },
                    }}
                />
            )
        case TagType.string.value:
            return (
                <>
                    <TextareaInput
                        {...inputProps}
                        autosize
                        defaultValue={defaultValue || undefined}
                        minRows={customField.min_rows || 1}
                    />
                    {customField.is_hyperlinkable && (
                        <TextareaInput
                            className={classes.urlText}
                            disabled={disabled}
                            name={`${inputProps.name}_url`}
                            label={customField.subfield_label || `External Link for ${inputProps.label}`}
                            placeholder={customField.subfield_placeholder || "http://www.quorum.us/"}
                            tooltip={
                                customField.subfield_tooltip ||
                                "Enter an optional URL to hyperlink this Custom Field's text value."
                            }
                            rules={{ validate: (url) => urlInputValidation(url) }}
                            minRows={1}
                            autosize
                        />
                    )}
                </>
            )
        case TagType.single_option_list.value:
            return (
                <Select
                    {...inputProps}
                    clearable
                    data={parseOptions(customField.options_new)}
                    defaultValue={defaultValue || undefined}
                />
            )
        case TagType.multi_options_list.value:
            return (
                <MultiSelect
                    {...inputProps}
                    clearable
                    data={parseOptions(customField.options_new)}
                    defaultValue={defaultValue || undefined}
                />
            )
        // TODO: Transform "true"/"false" to actual booleans
        case TagType.boolean.value:
            return (
                <Select
                    {...inputProps}
                    data={[
                        { value: "true", label: "Yes" },
                        { value: "false", label: "No" },
                    ]}
                    defaultValue={defaultValue != null ? defaultValue : undefined}
                    clearable
                    transform={{
                        // Read booleans as strings to map to select items
                        input: (input?: string | boolean | null) => {
                            if (input === false) {
                                return "false"
                            } else if (input === true) {
                                return "true"
                            } else {
                                return input
                            }
                        },
                        // Return strings as booleans
                        output: (output?: string | null) => {
                            if (output === "false") {
                                return false
                            } else if (output === "true") {
                                return true
                            } else {
                                return null
                            }
                        },
                    }}
                    rules={{
                        // The required validator just checks for truthiness, so we need a custom validator
                        validate: (value) => {
                            if (customField.is_required && value == null) {
                                return `${customField.name} is a required field`
                            }
                            return true
                        },
                    }}
                />
            )
        case TagType.date.value:
            return (
                <DatePickerInput
                    {...inputProps}
                    clearable
                    // TODO: This is a hack, but Dates are treated as UTC while DateTimes are treated as local
                    defaultValue={defaultValue ? new Date(`${defaultValue}T00:00:00`) : null}
                />
            )
        default:
            return null
    }
}

interface CustomFieldsFormSectionProps {
    editCustomFieldsButton?: React.ReactNode
    isEditing?: boolean
    onEditCustomFields: (e: React.MouseEvent) => void
    quorumDataType: number
}

export const CustomFieldsFormSection = ({
    editCustomFieldsButton = null,
    isEditing = true,
    onEditCustomFields,
    quorumDataType,
}: CustomFieldsFormSectionProps) => {
    const { isMobile } = useResponsiveSize()
    const customFields = useCustomFields(quorumDataType)
    const formName = DjangIO.app.models.QuorumDataType.by_value(quorumDataType).label

    const permissionForManagingCustomFields = checkPermission(
        DjangIO.app.models.PermissionType.can_access_custom_fields_management,
    )

    if (customFields.isLoading) {
        // TODO: Show a loading state here
        return null
    }

    return (
        <>
            {customFields.data.length ? (
                customFields.data.map((customField) => {
                    const parentCustomField = customFields.data.find(
                        (tag) => tag.id === CustomTag.idFromResourceUri(customField.conditional_parent_custom_field),
                    )
                    return (
                        <CustomField
                            key={customField.slug}
                            parentCustomFieldSlug={parentCustomField?.slug}
                            parentCustomFieldType={parentCustomField?.tag_type}
                            disabled={!isEditing}
                            {...customField}
                        />
                    )
                })
            ) : (
                <span>
                    Your organization does not have any custom fields created for {formName}. Click "Edit Custom Fields"
                    to create a new custom field.
                </span>
            )}

            {isEditing && permissionForManagingCustomFields && !isMobile && !editCustomFieldsButton && (
                <div>
                    <Button leftIcon={<Icon icon="pencil" />} onClick={onEditCustomFields} size="xs" variant="outline">
                        Edit Custom Fields
                    </Button>
                </div>
            )}

            {editCustomFieldsButton}
        </>
    )
}
